import { createContext, FC, PropsWithChildren, useCallback, useContext, useState } from "react";
import { PromptLibraryItemSource } from "@/modules/promptLibrary/promptLibrary.types";

interface PromptToApply {
  source: PromptLibraryItemSource;
  prompt: string;
}

interface PromptLibraryContextState {
  subscribe: (cb: (prompt: string) => void) => () => void;
  emit: (prompt: string) => void;
  openCreatePromptLibraryModal: (prompt: string, source: PromptLibraryItemSource) => void;
  closeCreatePromptLibraryModal: () => void;
  createPromptLibraryModalState: { prompt: string; source: PromptLibraryItemSource } | null;
  promptToApply: PromptToApply | null;
  setPromptToApply: (value: PromptToApply | null) => void;
}

const PromptLibraryContext = createContext<PromptLibraryContextState>({
  subscribe: () => () => {},
  emit: () => {},
  openCreatePromptLibraryModal: () => {},
  closeCreatePromptLibraryModal: () => {},
  createPromptLibraryModalState: null,
  promptToApply: null,
  setPromptToApply: () => {},
});

export const PromptLibraryContextProvider: FC<PropsWithChildren> = ({ children }) => {
  const [subscribers, setSubscribers] = useState<ReadonlyArray<(prompt: string) => void>>([]);
  const [createPromptLibraryModalState, setCreatePromptLibraryModalState] = useState<{
    prompt: string;
    source: PromptLibraryItemSource;
  } | null>(null);
  const [promptToApply, setPromptToApply] = useState<PromptToApply | null>(null);

  const subscribe = useCallback(
    (subscriber: (prompt: string) => void) => {
      setSubscribers(currentSubscribers => [...currentSubscribers, subscriber]);

      return () => setSubscribers(currentSubscribers => currentSubscribers.filter(item => item !== subscriber));
    },

    [setSubscribers],
  );

  const emit = useCallback(
    (prompt: string) => {
      for (const subscriber of subscribers) {
        subscriber(prompt);
      }
    },
    [subscribers],
  );

  const openCreatePromptLibraryModal = (prompt: string, source: PromptLibraryItemSource) => {
    setCreatePromptLibraryModalState({ prompt, source });
  };
  const closeCreatePromptLibraryModal = () => {
    setCreatePromptLibraryModalState(null);
  };

  return (
    <PromptLibraryContext.Provider
      value={{
        closeCreatePromptLibraryModal,
        createPromptLibraryModalState,
        emit,
        openCreatePromptLibraryModal,
        setPromptToApply,
        promptToApply,
        subscribe,
      }}
    >
      {children}
    </PromptLibraryContext.Provider>
  );
};

export const usePromptLibraryContext = () => {
  const context = useContext(PromptLibraryContext);

  if (!context) {
    throw new Error("usePromptLibraryContext must be used within a PromptLibraryContextProvider");
  }

  return context;
};
